/**
 * AutoCount - Cycle Count Page
 */

import { useState } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

interface CountRow {
  partNumber: string;
  name: string;
  expected: number;
  counted: number | null;
}

const initialRows: CountRow[] = [
  { partNumber: "BP-HC-2024-FR", name: "Front Brake Pads - Honda Civic 2016-2024", expected: 18, counted: 15 },
  { partNumber: "SP-NGK-IRIDIUM-4", name: "Iridium Spark Plugs - 4 Pack", expected: 55, counted: 58 },
  { partNumber: "BP-TC-2024-FR", name: "Front Brake Pads - Toyota Camry 2020-2024", expected: 24, counted: null },
  { partNumber: "OF-TY-2024", name: "Oil Filter - Toyota Universal", expected: 140, counted: null },
];

const statCardSx = { border: 1, borderColor: "divider", boxShadow: "none" } as const;

function InventoryCount(): JSX.Element {
  const [rows, setRows] = useState<CountRow[]>(initialRows);

  const countedRows = rows.filter((r) => r.counted !== null);
  const discrepancies = countedRows.filter((r) => r.counted !== r.expected).length;
  const progress = Math.round((countedRows.length / rows.length) * 100);

  const startNewSession = () => {
    setRows(rows.map((r) => ({ ...r, counted: null })));
  };

  const columns = [
    { Header: "Part Number", accessor: "partNumber", width: "15%" },
    { Header: "Name", accessor: "name", width: "30%" },
    { Header: "Expected", accessor: "expected", width: "12%" },
    { Header: "Counted", accessor: "counted", width: "12%" },
    { Header: "Variance", accessor: "variance", width: "12%" },
    { Header: "Status", accessor: "status", width: "12%" },
  ];

  const tableRows = rows.map((r) => {
    const variance = r.counted === null ? null : r.counted - r.expected;
    const status = r.counted === null ? "Pending" : variance !== 0 ? "Variance" : "Counted";
    return {
      partNumber: r.partNumber,
      name: r.name,
      expected: r.expected,
      counted: r.counted === null ? "—" : r.counted,
      variance:
        variance === null ? (
          "—"
        ) : (
          <MDTypography
            variant="caption"
            fontWeight="medium"
            color={variance === 0 ? "success" : "warning"}
          >
            {variance > 0 ? `+${variance}` : variance}
          </MDTypography>
        ),
      status: (
        <MDTypography
          variant="caption"
          fontWeight="medium"
          color={status === "Pending" ? "secondary" : status === "Variance" ? "warning" : "success"}
        >
          {status}
        </MDTypography>
      ),
    };
  });

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="h6" color="white">
                  Cycle Count
                </MDTypography>
                <MDButton variant="gradient" color="dark" size="small" onClick={startNewSession}>
                  <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                  &nbsp;New Count Session
                </MDButton>
              </MDBox>
              <MDBox p={3}>
                <Grid container spacing={2} mb={3}>
                  <Grid item xs={12} md={3}>
                    <Card sx={statCardSx}>
                      <MDBox p={2} textAlign="center">
                        <MDTypography variant="h4">{rows.length}</MDTypography>
                        <MDTypography variant="caption" color="text">Items to Count</MDTypography>
                      </MDBox>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Card sx={statCardSx}>
                      <MDBox p={2} textAlign="center">
                        <MDTypography variant="h4" color="success">{countedRows.length}</MDTypography>
                        <MDTypography variant="caption" color="text">Counted</MDTypography>
                      </MDBox>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Card sx={statCardSx}>
                      <MDBox p={2} textAlign="center">
                        <MDTypography variant="h4" color="warning">{discrepancies}</MDTypography>
                        <MDTypography variant="caption" color="text">Discrepancies</MDTypography>
                      </MDBox>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Card sx={statCardSx}>
                      <MDBox p={2} textAlign="center">
                        <MDTypography variant="h4">{progress}%</MDTypography>
                        <MDTypography variant="caption" color="text">Progress</MDTypography>
                      </MDBox>
                    </Card>
                  </Grid>
                </Grid>
                <DataTable
                  table={{ columns, rows: tableRows }}
                  isSorted={false}
                  entriesPerPage={{ defaultValue: 10, entries: [5, 10, 25, 50] }}
                  showTotalEntries={true}
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default InventoryCount;
